import Box16 from '@carbon/icons-react/es/box/16';
import Undo16 from '@carbon/icons-react/es/undo/16';
import { faArchive } from '@fortawesome/pro-duotone-svg-icons';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import {
  Container,
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { UnarchiveProjectModal } from 'components/molecules/project-actions-modal';
import { RoutePaths } from 'constant';
import { ArchivedProjectApiErrorData } from 'queries/error';
import { getProjectPlanUrl } from 'utils/urls';

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    padding: 30vh var(--layout-07) var(--layout-07) var(--layout-07);
  }
  ${Container} {
    width: auto;
    max-width: max-content;
  }
`;

const UnarchiveCtaMessage = styled.p`
  padding-top: var(--layout-03);
  margin-bottom: 0;
`;

interface Props {
  error: ArchivedProjectApiErrorData;
}

export const ArchivedProjectErrorPage: FC<Props> = ({ error }) => {
  const history = useHistory();
  const [showUnarchiveModal, setShowUnarchiveModal] = useState<boolean>(false);

  const closeArchiveModal = () => {
    setShowUnarchiveModal(false);
  };
  const onUnarchiveProject = () => {
    setShowUnarchiveModal(true);
  };

  const errorResponse = error.response?.data;

  const projectId = errorResponse?.data.projectId;
  const projectName = errorResponse?.data.projectName;
  const canUnarchive = errorResponse?.data.canUnarchive;

  const cta = canUnarchive ? (
    <EmptyState.CallToAction.Primary
      icon={<Box16 />}
      onClick={onUnarchiveProject}
    >
      <FormattedMessage id="unarchive" />
    </EmptyState.CallToAction.Primary>
  ) : (
    <Link to={RoutePaths.home}>
      <EmptyState.CallToAction.Primary icon={<Undo16 />}>
        <FormattedMessage id="go_back_home" />
      </EmptyState.CallToAction.Primary>
    </Link>
  );

  const onUnarchiveSuccess = () => {
    if (projectId) {
      history.push(getProjectPlanUrl(ProjectId(projectId)));
      history.go(0);
    }
  };

  return (
    <EmptyStateContainer $transparent>
      <EmptyState
        image={<EmptyStateIcon icon={faArchive} />}
        title={<FormattedMessage id="project.archive.error.404.title" />}
        message={
          <>
            <FormattedMessage
              id="project.archive.error.404.info"
              values={{
                projectName: projectName || '',
                b: (value) => <strong>{value}</strong>,
              }}
            />
            {canUnarchive ? (
              <UnarchiveCtaMessage>
                <FormattedMessage id="project.archive.error.404.info_cta" />
              </UnarchiveCtaMessage>
            ) : null}
          </>
        }
        primaryCTA={cta}
      />
      {projectId && projectName && canUnarchive ? (
        <UnarchiveProjectModal
          onUnarchiveSuccess={onUnarchiveSuccess}
          isVisible={showUnarchiveModal}
          projectId={projectId}
          projectName={projectName}
          closeModal={closeArchiveModal}
          hideUnarchiveSuccessAction
        />
      ) : null}
    </EmptyStateContainer>
  );
};
